import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TextAlignLeft, LinkSimple, Microphone, VideoCamera, UploadSimple, Plus, X, Sparkle, ArrowRight, Camera } from "@phosphor-icons/react";
import { parseText, parseUrl, parseManual, parseAudio, parseVideo, parseImage } from "../lib/api";

const MODES = [
  { id: "manual", label: "Manual", icon: Plus, hint: "Add ingredients row by row", bg: "bg-[#0066FF] hover:bg-[#0052CC]" },
  { id: "text", label: "Text / Paste", icon: TextAlignLeft, hint: "Paste any recipe text — AI parses it", bg: "bg-[#6B8E23] hover:bg-[#5A7A1C]" },
  { id: "url", label: "Web link", icon: LinkSimple, hint: "Blogs, YouTube pages, article URLs", bg: "bg-[#E6B800] hover:bg-[#CCA300]" },
  { id: "audio", label: "Audio", icon: Microphone, hint: "Upload a voice memo of your recipe", bg: "bg-[#F97316] hover:bg-[#EA580C]" },
  { id: "video", label: "Video", icon: VideoCamera, hint: "Upload a cooking video (mp4 / mov)", bg: "bg-[#EC4899] hover:bg-[#DB2777]" },
  { id: "image", label: "Photo", icon: Camera, hint: "Snap a cookbook page — Gemini reads it", bg: "bg-[#8B5CF6] hover:bg-[#7C3AED]" },
];

export default function MultiModalInput() {
  const nav = useNavigate();
  const [mode, setMode] = useState("manual");
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [servings, setServings] = useState(1);
  const [rows, setRows] = useState([{ rid: crypto.randomUUID(), name: "", quantity: "", unit: "g" }]);
  const [recipeName, setRecipeName] = useState("My Recipe");
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const goToRecipe = (recipe) => nav(`/recipe/${recipe.id}`);

  // Listen for the hero-demo click → prefill text input & switch to Text tab.
  useEffect(() => {
    const handler = (e) => {
      const recipe = e?.detail?.recipe;
      if (!recipe) return;
      setMode("text");
      setText(recipe);
      toast.success("Demo recipe pulled in — press Calculate.", { icon: "🧪" });
    };
    window.addEventListener("demo:freeze-prefill", handler);
    return () => window.removeEventListener("demo:freeze-prefill", handler);
  }, []);

  const submit = async () => {
    setLoading(true);
    try {
      let recipe;
      if (mode === "manual") {
        const valid = rows.filter(r => r.name && Number(r.quantity) > 0)
          .map(r => ({ name: r.name, quantity: Number(r.quantity), unit: r.unit }));
        if (!valid.length) { toast.error("Add at least one ingredient"); setLoading(false); return; }
        recipe = await parseManual(recipeName || "My Recipe", Number(servings) || 1, valid);
      } else if (mode === "text") {
        if (!text.trim()) { toast.error("Paste a recipe"); setLoading(false); return; }
        recipe = await parseText(text, Number(servings) || 1);
      } else if (mode === "url") {
        if (!url.trim()) { toast.error("Enter a URL"); setLoading(false); return; }
        recipe = await parseUrl(url, Number(servings) || 1);
      } else if (mode === "audio") {
        if (!file) { toast.error("Choose an audio file"); setLoading(false); return; }
        recipe = await parseAudio(file, Number(servings) || 1);
      } else if (mode === "video") {
        if (!file) { toast.error("Choose a video file"); setLoading(false); return; }
        recipe = await parseVideo(file, Number(servings) || 1);
      } else if (mode === "image") {
        if (!file) { toast.error("Choose an image file"); setLoading(false); return; }
        recipe = await parseImage(file, Number(servings) || 1);
      }
      toast.success(`Parsed: ${recipe.name}`);
      goToRecipe(recipe);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend returned a witty text-fallback prompt for a blocked/unparseable link
      if (detail && typeof detail === "object" && detail.fallback === "text") {
        setMode("text");
        const prompt = `[Recipe from: ${detail.url}]\n\n(Paste the ingredients here — we couldn't fetch them automatically.)`;
        setText(prompt);
        toast(detail.message, { duration: 7000, icon: "🧪" });
      } else {
        const msg = (typeof detail === "string" && detail) || e.message || "Something went wrong";
        toast.error(`Failed: ${msg}`);
      }
    } finally { setLoading(false); }
  };

  const updateRow = (i, key, val) => {
    const next = [...rows]; next[i] = { ...next[i], [key]: val }; setRows(next);
  };
  const addRow = () => setRows([...rows, { rid: crypto.randomUUID(), name: "", quantity: "", unit: "g" }]);
  const rmRow = (i) => setRows(rows.filter((_, k) => k !== i));

  return (
    <div className="border border-border bg-card" data-testid="multimodal-input">
      {/* mode selector */}
      <div className="grid grid-cols-6 border-b border-border" data-testid="mode-tabs">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              data-testid={`mode-${m.id}`}
              className={`flex flex-col items-center justify-center gap-1.5 py-4 border-r border-border last:border-r-0 transition-colors duration-200 ${
                active ? "bg-[#C8FF3F] text-black font-semibold" : `${m.bg} text-white`
              }`}
            >
              <Icon size={18} weight={active ? "fill" : "regular"} />
              <span className="font-mono text-[10px] uppercase tracking-[0.15em]">{m.label}</span>
            </button>
          );
        })}
      </div>

      {/* body */}
      <div className="p-6 md:p-8 space-y-6">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Input · {mode}</div>
            <div className="text-sm text-muted-foreground mt-1">{MODES.find(m => m.id === mode)?.hint}</div>
          </div>
          <div className="flex items-center gap-3">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Servings</label>
            <input
              type="number" min="1" value={servings}
              onChange={e => setServings(e.target.value)}
              data-testid="servings-input"
              className="w-16 h-9 px-2 border border-border bg-background font-mono text-right focus:outline-none focus:border-primary transition-colors duration-200"
            />
          </div>
        </div>

        {mode === "manual" && (
          <div className="space-y-3" data-testid="manual-form">
            <input
              value={recipeName} onChange={e => setRecipeName(e.target.value)}
              data-testid="recipe-name-input"
              placeholder="Recipe name"
              className="w-full h-11 px-3 border border-border bg-background font-display font-medium text-lg focus:outline-none focus:border-primary transition-colors duration-200"
            />
            <div className="grid grid-cols-[1fr_120px_120px_40px] gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground pb-1 border-b border-border">
              <div>Ingredient</div><div className="text-right">Qty</div><div>Unit</div><div />
            </div>
            {rows.map((r, i) => (
              <div key={r.rid} className="grid grid-cols-[1fr_120px_120px_40px] gap-2" data-testid={`ingredient-row-${i}`}>
                <input value={r.name} onChange={e => updateRow(i, "name", e.target.value)}
                  data-testid={`ingredient-name-${i}`}
                  placeholder="e.g. chicken breast"
                  className="h-10 px-3 border border-border bg-background focus:outline-none focus:border-primary transition-colors duration-200" />
                <input type="number" value={r.quantity} onChange={e => updateRow(i, "quantity", e.target.value)}
                  data-testid={`ingredient-qty-${i}`}
                  placeholder="0"
                  className="h-10 px-3 border border-border bg-background font-mono text-right focus:outline-none focus:border-primary transition-colors duration-200" />
                <select value={r.unit} onChange={e => updateRow(i, "unit", e.target.value)}
                  data-testid={`ingredient-unit-${i}`}
                  className="h-10 px-2 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200">
                  {["g","kg","mg","oz","lb","ml","l","cup","tbsp","tsp","piece","slice","clove"].map(u =>
                    <option key={u} value={u}>{u}</option>)}
                </select>
                <button onClick={() => rmRow(i)} disabled={rows.length === 1}
                  data-testid={`remove-ingredient-${i}`}
                  className="h-10 border border-border hover:bg-muted disabled:opacity-40 flex items-center justify-center transition-colors duration-200">
                  <X size={14} />
                </button>
              </div>
            ))}
            <button onClick={addRow} data-testid="add-ingredient-btn"
              className="h-10 px-4 border border-dashed border-border hover:border-foreground hover:bg-muted flex items-center gap-2 text-sm transition-colors duration-200">
              <Plus size={14} /> Add ingredient
            </button>
          </div>
        )}

        {mode === "text" && (
          <textarea
            value={text} onChange={e => setText(e.target.value)}
            data-testid="text-input"
            placeholder={"Paste any recipe. Example:\n\nSpaghetti Bolognese\n\n- 500g spaghetti\n- 400g ground beef\n- 2 cups tomato sauce\n- 1 onion, chopped\n- 3 cloves garlic\n- 2 tbsp olive oil"}
            className="w-full min-h-[280px] p-4 border border-border bg-background font-mono text-sm leading-relaxed focus:outline-none focus:border-primary transition-colors duration-200"
          />
        )}

        {mode === "url" && (
          <div className="space-y-3" data-testid="url-form">
            <input
              value={url} onChange={e => setUrl(e.target.value)}
              data-testid="url-input"
              placeholder="https://www.example.com/some-recipe"
              className="w-full h-12 px-4 border border-border bg-background font-mono focus:outline-none focus:border-primary transition-colors duration-200"
            />
            <p className="text-xs text-muted-foreground">
              Paste any recipe blog or article URL. The scraper fetches the page and our AI extracts the recipe.
            </p>
          </div>
        )}

        {(mode === "audio" || mode === "video" || mode === "image") && (
          <div data-testid={`${mode}-form`}>
            <button
              onClick={() => fileRef.current?.click()}
              data-testid={`${mode}-dropzone`}
              className="w-full min-h-[220px] border-2 border-dashed border-border hover:border-primary hover:bg-muted/40 flex flex-col items-center justify-center gap-3 transition-colors duration-200"
            >
              <UploadSimple size={36} weight="regular" className="text-muted-foreground" />
              <div className="font-display font-medium text-lg">
                {file ? file.name : `Drop ${mode} or click to upload`}
              </div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {mode === "audio" ? "MP3 · WAV · M4A · OGG" :
                 mode === "video" ? "MP4 · MOV · WEBM (up to ~50MB)" :
                 "JPG · PNG · HEIC · WEBP (cookbook pages, notes)"}
              </div>
            </button>
            <input
              type="file" ref={fileRef} className="hidden"
              accept={mode === "audio" ? "audio/*" : mode === "video" ? "video/*" : "image/*"}
              data-testid={`${mode}-file-input`}
              onChange={e => setFile(e.target.files?.[0] || null)}
            />
          </div>
        )}

        <button
          onClick={submit}
          disabled={loading}
          data-testid="calculate-btn"
          className="w-full h-14 bg-primary text-primary-foreground font-display font-bold text-lg tracking-tight hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-3 transition-colors duration-200"
        >
          {loading ? (
            <><Sparkle size={20} className="animate-pulse" /> Analyzing recipe…</>
          ) : (
            <>Calculate Nutrients <ArrowRight size={20} weight="bold" /></>
          )}
        </button>
      </div>
    </div>
  );
}
